import PlayIcon from "@/components/icons/exerciseV2/PlayIcon";
import PauseIcon from "@/components/icons/PauseIcon.tsx";
import { useExerciseState } from "@/contexts/exercise/Context";
import { useAudioPlayer } from "@/utils/hooks/useAudioPlayer";
import React, { useEffect, useRef, useState } from "react";
import ReactPlayer from "react-player";

interface AudioPlayerV2Props {
  src: string;
}

const AudioPlayerV2: React.FC<AudioPlayerV2Props> = ({ src }) => {
  const { isExerciseStarted } = useExerciseState();
  const { playing, togglePlay, stopPlay, setAudio, audio, isLoaded } =
    useAudioPlayer();

  const reactPlayerRef = useRef<ReactPlayer | null>(null);

  const [progress, setProgress] = useState(0);

  useEffect(() => {
    setAudio(new Audio(src));
  }, [src, setAudio]);

  const handlePlayPause = () => {
    togglePlay().catch((error) => console.error("Error playing audio:", error));
  };

  useEffect(() => {
    const handleTimeUpdate = () => {
      if (audio) {
        const currentProgress = (audio.currentTime / audio.duration) * 100;
        setProgress(currentProgress);
      }
    };

    audio?.addEventListener("timeupdate", handleTimeUpdate);

    return () => {
      audio?.removeEventListener("timeupdate", handleTimeUpdate);
    };
  }, [audio]);

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
  };

  useEffect(() => {
    if (audio) {
      const handleLoadedMetadata = () => {
        if (reactPlayerRef.current) {
          reactPlayerRef.current?.seekTo(audio.currentTime, "seconds");
        }
      };

      audio.addEventListener("loadedmetadata", handleLoadedMetadata);

      return () => {
        audio.removeEventListener("loadedmetadata", handleLoadedMetadata);
      };
    }
  }, [audio]);

  useEffect(() => {
    return () => {
      stopPlay().catch((error) => console.error("Error playing audio:", error));
    };
  }, [stopPlay]);

  return (
    <div
      title={isExerciseStarted ? "" : "Start the exercise to play audio"}
      className="flex flex-col w-full"
    >
      <div className="w-full flex items-center gap-3 px-3.5 py-2 bg-white shadow-lg rounded-[10px]">
        <button
          onClick={handlePlayPause}
          disabled={!isExerciseStarted || !isLoaded}
        >
          {isExerciseStarted ? (
            playing ? (
              <PauseIcon className="w-4 h-4 fill-brand-secondary" />
            ) : (
              <PlayIcon className="w-4 h-4" />
            )
          ) : (
            ""
          )}
        </button>

        <div className="my-auto text-xs text-black font-medium">
          {formatTime(audio?.currentTime || 0)} /{" "}
          {formatTime(audio?.duration || 0)}
        </div>

        <div className="flex flex-col flex-1 justify-center my-auto rounded-[200px]">
          <div className="flex flex-col justify-center items-start bg-zinc-300">
            <div
              className="shrink-0 h-1 bg-violet-500 rounded-[200px]"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        <ReactPlayer
          url={src}
          ref={reactPlayerRef}
          width="0"
          style={{ display: "none" }}
          height="0"
          playing={playing}
        />
      </div>
    </div>
  );
};

export default AudioPlayerV2;
